import { useCallback, useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { api, errorMessage } from "../lib/api";
import { ProspectRow } from "./ProspectRow";

const LEVELS = Array.from({ length: 11 }, (_, index) => index / 2);

export const SearchResultsSheet = ({ searchId, session, onClose, onUpdated, onOpenVenue }) => {
  const [result, setResult] = useState(null);
  const [groups, setGroups] = useState([]);
  const [person, setPerson] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [moreBusy, setMoreBusy] = useState(false);
  const [bulkChoice, setBulkChoice] = useState("");
  const load = useCallback(() => { if (!searchId) return Promise.resolve();
    return api.get(`/searches/${searchId}/results`).then(({ data }) => {
      setResult(data); setPerson((before) => before || data.suggested_id || ""); }); }, [searchId]);
  useEffect(() => {
    if (!searchId) return;
    setResult(null); setNotice(""); setError("");
    load().catch((e) => setError(errorMessage(e)));
    api.get("/map/groups").then(({ data }) => setGroups(data.groups)).catch((e) => setError(errorMessage(e)));
  }, [searchId, load]);
  const decide = async (salespersonId) => {
    setBusy(true); setError("");
    try { const { data } = await api.post(`/searches/${searchId}/assign`, { salesperson_id: salespersonId },
      { headers: { "X-CSRF-Token": session.csrf_token } });
      setNotice(salespersonId ? `${data.assigned_count} locali assegnati.` : "I locali aggiunti restano Da assegnare.");
      await load(); onUpdated();
    } catch (e) { setError(errorMessage(e)); }
    finally { setBusy(false); }
  };
  const added = async () => { await load(); onUpdated(); };
  const addAll = async () => {
    if (result.search.keywords.length > 1 && !bulkChoice) { setBulkChoice(result.search.keywords[0]); return; }
    setBulkBusy(true); setError("");
    try { const { data } = await api.post(`/searches/${searchId}/prospects/bulk-add`,
      { activity_type: bulkChoice || result.search.keywords[0] },
      { headers: { "X-CSRF-Token": session.csrf_token } });
      setNotice(`${data.added} locali aggiunti in ${data.activity_type}${data.skipped ? ` · ${data.skipped} già presenti` : ""}.`);
      await load(); onUpdated();
    } catch (e) { setError(errorMessage(e)); }
    finally { setBulkBusy(false); }
  };
  const more = async () => {
    setMoreBusy(true); setError("");
    try {
      await api.post(`/searches/${searchId}/more`, {}, { headers: { "X-CSRF-Token": session.csrf_token } });
      for (let attempt = 0; attempt < 70; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 4000));
        const { data } = await api.get(`/searches/${searchId}`);
        if (!data.loading_more) {
          await load(); onUpdated();
          if (data.more_error) setError(data.more_error);
          else setNotice(`${data.results_count} nuovi locali visualizzati.`);
          return;
        }
      }
      setError("La ricerca è ancora in corso. Apri lo storico tra poco.");
    } catch (e) { setError(errorMessage(e)); }
    finally { setMoreBusy(false); }
  };
  return <Sheet open={Boolean(searchId)} onOpenChange={(next) => { if (!next) onClose(); }}>
    <SheetContent side="bottom" className="hud-sheet" closeTestId="search-results-close"
      data-testid="search-results-sheet" description="Anteprima dei nuovi locali trovati dalla ricerca.">
      <div className="sheet-grab" aria-hidden="true" /><SheetHeader><SheetTitle className="sheet-title results-title" data-testid="search-results-title">{result?.search.results_count ?? "…"} NUOVI LOCALI</SheetTitle></SheetHeader>
      {result && <div className="search-results-body" data-testid="search-results-content">
        <div className="result-metrics"><div><strong data-testid="results-3stars">{result.stars3_count}</strong><span>★★★+</span></div>
          <div><strong data-testid="results-2stars">{result.stars2_count}</strong><span>★★+</span></div>
          <div><strong data-testid="results-new">{result.search.new_count}</strong><span>nuovi</span></div>
          <div><strong data-testid="results-existing">{result.search.existing_count}</strong><span>già noti</span></div></div>
        <div className="result-meta-counts"><span data-testid="results-chain-exclusions">{result.search.excluded_chains_count || 0} catene escluse</span>
          <span data-testid="results-meta-summary">Meta: {result.meta_summary?.checked || 0} controllati su {result.meta_summary?.with_social || 0} con social · {result.meta_summary?.with_ads || 0} con inserzioni</span></div>
        {result.prospects?.some((item) => !item.added_venue_id) && <div className="results-bulk" data-testid="results-bulk-actions">
          {result.search.keywords.length > 1 && bulkChoice && <div className="form-field"><label htmlFor="results-bulk-type">AGGIUNGI TUTTI IN</label>
            <select id="results-bulk-type" value={bulkChoice} onChange={(e) => setBulkChoice(e.target.value)}
              data-testid="results-bulk-type">{result.search.keywords.map((word) => <option key={word} value={word}>{word}</option>)}</select></div>}
          <button type="button" className="hud-button results-add-all" disabled={bulkBusy} onClick={addAll}
            data-testid="results-add-all">{bulkBusy ? "Aggiunta in corso…" : `Aggiungi tutti (${result.prospects.filter((item) => !item.added_venue_id).length})`}</button>
        </div>}
        <div className="result-distribution" data-testid="results-distribution"><h3 className="section-label">DISTRIBUZIONE VOTI · SOLO NUOVI</h3>
          <div>{LEVELS.map((level) => <span key={level} data-testid={`rating-level-${level.toString().replace(".", "-")}`}><small>{level.toLocaleString("it-IT")}</small><strong>{result.search.distribution?.[String(level)] || 0}</strong></span>)}</div></div>
        {result.prospects?.length ? <div className="prospect-list" data-testid="prospect-list">
          <h3 className="section-label">ANTEPRIMA · SCEGLI QUALI AGGIUNGERE</h3>
          {result.prospects.map((item, index) => <ProspectRow key={item.id} item={item} rank={index + 1}
            groups={groups} searchId={searchId} csrfToken={session.csrf_token} onAdded={added} onOpenVenue={onOpenVenue} />)}</div>
          : result.best.length ? result.best.map((item) => <button type="button" key={item.venue_id}
            className="result-venue" onClick={() => onOpenVenue(item.venue_id)} data-testid={`result-venue-${item.venue_id}`}>
            <strong>{item.name}</strong><span>★ {item.rating} ›</span></button>)
            : <p className="today-empty" data-testid="results-empty">Nessun nuovo locale trovato.</p>}
        {result.search.has_more && <button type="button" className="hud-button hud-secondary results-more" disabled={moreBusy}
          onClick={more} data-testid="results-more-button">{moreBusy ? "Ricerca altri locali…" : "Carica altri 50"}</button>}
        {!result.search.assignment_decision && result.added_count > 0 && <div className="result-decision"><div className="form-field"><label htmlFor="results-person">COMMERCIALE</label>
          <select id="results-person" value={person} onChange={(e) => setPerson(e.target.value)} data-testid="results-person-select"><option value="">Seleziona</option>
            {result.salespeople.map((p) => <option key={p.id} value={p.id}>{p.name}{p.id === result.suggested_id ? " · suggerito" : ""}</option>)}</select></div>
          <button type="button" className="hud-button" disabled={!person || busy} onClick={() => decide(person)}
            data-testid="results-assign-button">Assegna {result.added_count} locali aggiunti</button>
          <button type="button" className="hud-button hud-secondary" disabled={busy} onClick={() => decide(null)}
            data-testid="results-unassigned-button">Lasciali Da assegnare</button></div>}
      </div>}
      {notice && <p role="status" className="venue-success" data-testid="results-notice">{notice}</p>}
      {error && <p role="alert" className="form-error" data-testid="results-error">{error}</p>}
    </SheetContent>
  </Sheet>;
};